import { delay } from '../../common/delay.js'
import { t } from '../../common/i18n.js'
import type { NotionProject } from '../../common/types/notion.js'
import { INTEGRATIONS_CHANGED_EVENT } from '../components/integrations-gate/integrations-gate-logic.js'
import { DataState, View } from '../types.js'
import { fetchMyRole } from '../views/portal/utils/portal-api.js'
import { syncThemeFromServer } from './theme.js'

/**
 * Refresh the open Q12 campaign state (banner, sidebar button): which campaign
 * is open for this user and whether they have already answered it. Called at
 * boot, on every dashboard visit and right after a survey submission.
 */
export const refreshActiveCampaign = async (view: View) => {
    const { state, api } = view
    try {
        const res = await api.playtime.getActiveCampaign()
        state.hasActiveCampaign = res.data !== null
        state.activeCampaignId = res.data?.campaign.id
        state.activeCampaignEnd = res.data?.campaign.end
        state.activeCampaignBannerText = res.data?.campaign.banner_text ?? null
        state.activeCampaignBannerTextEn = res.data?.campaign.banner_text_en ?? null
        state.activeCampaignAnswered = res.data?.answered ?? false
        view.update()
    } catch {
        // Non-critical — the dashboard simply shows no Q12 strip.
    }
}

export const loadEatStatsIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.eat.stats, api.eat.getStats)
}

export const loadEatOrdersIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.eat.orders, api.eat.getMyOrders)
}

export const loadEatMealsIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.eat.meals, api.eat.getMeals)
}

export const loadEatSettingsIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.eat.settings, api.eat.getSettings)
}

const loadDataIfNeeded = <Data, DS extends DataState<Data>>(
    view: View,
    dataState: DS,
    load: () => Promise<Data>,
) => {
    const { data: remoteData, isLoading } = dataState
    const maxRetries = 3

    if (!remoteData && !isLoading && (dataState.failCount ?? 0) < maxRetries) {
        void loadData(view, dataState, load)
    }

    return dataState
}

const loadData = async <Data, DS extends DataState<Data>>(
    view: View,
    dataState: DS,
    load: () => Promise<Data>,
) => {
    const { update } = view

    dataState.isLoading = true
    update()

    await delayIfFailed(dataState.failCount)

    try {
        dataState.data = await load()
        dataState.failCount = 0
    } catch (error) {
        // Client errors (4xx) are permanent — stop retrying immediately
        const is4xx = error instanceof Error && /status 4\d{2}/.test(error.message)
        dataState.failCount = is4xx ? Infinity : (dataState.failCount ?? 0) + 1

        // Silently log — do NOT call handleError here since it triggers
        // view.update() which causes a render cascade with the finally block
        if (dataState.failCount <= 1) {
            console.warn('[loadData] Failed to load:', (error as Error).message)
        }
    } finally {
        dataState.isLoading = false
        update()
    }
}

const delayIfFailed = async (failCount: number | undefined) => {
    if (typeof failCount === 'number' && failCount > 0) {
        // Wait 2-10 seconds
        const delayMs = Math.min(failCount, 5) * 2000
        await delay(delayMs)
    }
}

export const getEmptyDataState = <T>(): DataState<T> => ({
    data: undefined,
    isLoading: false,
    failCount: 0,
})

export const loadSignaturesIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.signatures, api.signatures.getSignatures)
}

export const loadMyProfileIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.me, api.me.getProfile)
}

export const loadPortalRoleIfNeeded = (view: View) => {
    const { state } = view
    return loadDataIfNeeded(view, state.portalRole, async () => {
        const res = await fetchMyRole()
        if (!res.ok) throw new Error(res.error)
        // Server on teema tõeallikas üle seadmete; localStorage'i koopia oli vaid
        // esimese renderi jaoks.
        syncThemeFromServer(view, res.data.themePreference ?? 'system')
        return {
            themePreference: res.data.themePreference ?? 'system',
            myEmployeeId: res.data.employeeId,
            googleDriveUrl: res.data.googleDriveUrl ?? null,
            avatarUrl: res.data.avatarUrl ?? null,
            impersonation: res.data.impersonation ?? null,
            permissions: res.data.permissions ?? { workspace: [], profile: [], admin: [] },
            timeAwayManageScope: res.data.timeAwayManageScope ?? null,
        }
    })
}

export const reloadSignatures = (view: View) => {
    const { state, api } = view
    state.signatures.data = undefined
    state.signatures.isLoading = false
    state.signatures.failCount = 0
    void loadData(view, state.signatures, api.signatures.getSignatures)
    return state.signatures
}

export const loadPlaytimeProfileIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.playtime.profile, async () => {
        const res = await api.playtime.getProfile()
        return res.data
    })
}

export const reloadPlaytimeProfile = (view: View) => {
    const { state, api } = view
    state.playtime.profile.data = undefined
    state.playtime.profile.isLoading = false
    state.playtime.profile.failCount = 0
    void loadData(view, state.playtime.profile, async () => {
        const res = await api.playtime.getProfile()
        return res.data
    })
    return state.playtime.profile
}

/**
 * Refresh the profile without blanking it: the current badges stay on screen (so an
 * open badge modal survives) and the fresh data is swapped in when it arrives.
 */
export const refreshPlaytimeProfile = async (view: View) => {
    const { state, api } = view
    try {
        const res = await api.playtime.getProfile()
        state.playtime.profile.data = res.data
        view.update()
    } catch {
        // keep what we have; the next full load retries
    }
}

export const loadPlaytimeLeaderboardIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.playtime.leaderboard, async () => {
        const res = await api.playtime.getLeaderboard()
        return res.data
    })
}

export const loadBalancetimeAuthAndData = async (view: View) => {
    const { state, api } = view
    const bt = state.balancetime

    try {
        const urlParams = new URLSearchParams(window.location.search)
        const oauthCode = urlParams.get('code')

        if (oauthCode) {
            await api.playtime.saveHarvestCallback(oauthCode)
            window.history.replaceState({}, '', window.location.pathname)
            // ?code= is gone now, so the gate's re-fetch reads a clean URL and
            // sees the freshly-saved token instead of re-showing the modal.
            window.dispatchEvent(new Event(INTEGRATIONS_CHANGED_EVENT))
        }

        const res = await api.playtime.getBalanceTimeAuthStatus()
        bt.authStatus = res.data
        if (res.data.isAuthenticated) {
            await loadBalancetimeData(view)
        } else {
            bt.isLoading = false
            view.update()
        }
    } catch {
        bt.isLoading = false
        view.update()
    }
}

export const loadBalancetimeData = async (view: View, params?: Record<string, string>) => {
    const { state, api } = view
    const bt = state.balancetime

    bt.isLoading = true
    bt.loadError = null
    view.update()

    try {
        const queryParams: Record<string, string> = {
            range_type: bt.rangeType,
            offset: String(bt.offset),
        }
        if (bt.selectedUser) queryParams.selected_user = bt.selectedUser
        // Explicit params override state-derived defaults (avoids stale closure on user switch)
        Object.assign(queryParams, params ?? {})
        const res = await api.playtime.getBalanceTime(queryParams)
        bt.data = res.data
    } catch (err) {
        console.error('Failed to load balancetime data:', err)
        bt.loadError =
            err instanceof Error && 'sentryError' in err
                ? (err as Error & { sentryError: Error }).sentryError.message
                : t.playtime.balancetime.loadError(state.lang)
    } finally {
        bt.isLoading = false
        view.update()
    }
}

export const loadPortalLinksIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.portalLinks, async () => {
        const res = await api.portal.getLinks()
        return res.data
    })
}

export const loadMyLeaveBalanceIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.myLeaveBalance, async () => {
        const res = await api.portal.getMyLeaveBalance()
        return res.data
    })
}

export const loadUrlLinksIfNeeded = (view: View) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.urlShortener.links, async () => {
        const res = await api.urlShortener.getLinks()
        return res.data
    })
}

export const reloadUrlLinks = (view: View) => {
    const { state } = view
    state.urlShortener.links.data = undefined
    state.urlShortener.links.isLoading = false
    state.urlShortener.links.failCount = 0
    return loadUrlLinksIfNeeded(view)
}

export const loadUrlLinkDetailIfNeeded = (view: View, id: number) => {
    const { state, api } = view
    const detailState = state.urlShortener.detail

    // Reset if navigating to a different link
    if (state.urlShortener.currentLinkId !== id) {
        state.urlShortener.currentLinkId = id
        detailState.data = undefined
        detailState.isLoading = false
        detailState.failCount = 0
        state.urlShortener.accessLogs.data = undefined
        state.urlShortener.accessLogs.isLoading = false
        state.urlShortener.accessLogs.failCount = 0
        state.urlShortener.accessLogsPage = 1
    }

    return loadDataIfNeeded(view, detailState, async () => {
        const res = await api.urlShortener.getLink(id)
        return res.data
    })
}

export const loadUrlAccessLogsIfNeeded = (view: View, id: number) => {
    const { state, api } = view
    return loadDataIfNeeded(view, state.urlShortener.accessLogs, async () => {
        const page = state.urlShortener.accessLogsPage
        return api.urlShortener.getAccessLogs(id, page)
    })
}

export const loadWorktimeProjectsIfNeeded = (view: View) => {
    const { state } = view
    return loadDataIfNeeded(view, state.worktime.projects, async () => {
        const res = await fetch('/api/portal/projects')
        if (!res.ok) throw new Error(`status ${res.status}`)
        const json = (await res.json()) as { data?: NotionProject[] }
        return json.data ?? []
    })
}
